import { EnvironmentManager } from "../EnvironmentManager/EnvironmentManager";
import { EnvironmentType } from "../EnvironmentManager/EnvironmentType";

export class ServerConfig {

    public static shared = new ServerConfig();

    // Domain names for different environments
    productionDomainName = 'https://api.geniegig.com/'
    stagingDomainName = 'https://api.staging.geniegig.com/'
    localhostDomainName = 'http://localhost:8080/' // only for dev purposes

    // API versioning
    currentAPIVersion = 'v1'

    // Base URLs
    baseURL = this.baseDomainName + 'api/' + this.currentAPIVersion + '/';
    baseServiceDBURL = this.baseDomainName + 'service-db/';

    streamChatApiKey = EnvironmentManager.isProdEnv() ? "rns64spp8b6w" : "ketzmzs3g9ex";

    get baseDomainName(): string {
        const previewURL = process.env.REACT_APP_BACKEND_URL;

        if (previewURL) {
            return previewURL.endsWith('/') ? previewURL : previewURL + '/';
        }

        const currentEnv = EnvironmentManager.getCurrentEnv()
        switch (currentEnv) {
            case EnvironmentType.development:
                return this.localhostDomainName
            case EnvironmentType.staging:
                return this.stagingDomainName
            case EnvironmentType.production:
                return this.productionDomainName
            
            default:
                return this.stagingDomainName
        }
    }
    
    // auth API URLs
    authAPIs = {
        registerAPI: this.baseURL + 'register/',
        loginAPI: this.baseURL + 'login/',
        googleAuthAPI: this.baseURL + 'google-auth/',
        requestOtpForEmailVerificationAPI: this.baseURL + 'email-verification/request-otp/'
    }

    // buyer API URLs
    buyerAPIs = {
        createBuyerAPI: this.baseURL + 'create-buyer/',
        updateBuyer: this.baseURL + 'update-buyer/',
        getBuyerByAuthTokenAPI: this.baseURL + 'get-buyer-by-auth-token/',
        getBuyerPublicByIdAPI: (Id: string): string => { return this.baseURL + 'public-profile/buyer/' + Id },
        sendOtpResetPasswordAPI: this.baseURL + 'send-otp/reset-password/',
        verifyOtpResetPasswordAPI: this.baseURL + 'verify-otp/reset-password/',
        favouritesAPI: this.baseURL + 'favourites/'
    }

    // categories API URLs
    categoriesAPIs = {
        categoriesAPI: this.baseURL + 'categories/',
        categoriesWithSubcategoriesAPI: this.baseURL + 'categories-with-subcategories/',
        categoryAPI: this.baseURL + 'category/',
    }

    // Gig API URLs
    gigAPIS = {
        createGigAPI: this.baseURL + 'create-gig/',
        editGigAPI: this.baseURL + 'edit-gig/',
        getGigByIDAPI: this.baseURL + 'get-gig-by-id/',
        getGigsBySellerAPI: this.baseURL + 'get-gigs-by-seller/',
        getGigsBySubcategoryAPI: this.baseURL + 'get-gigs-by-subcategory/',
        deleteGigsByIDAPI: this.baseURL + 'delete-gig/',
    }


    // order API URLs
    orderAPIs = {
        getOrderByBuyerIDAPI: this.baseURL + 'get-order-by-buyer-id/',
        getOrderBySellerIDAPI: this.baseURL + 'get-order-by-seller-id/',
        approveOrderAPI: (orderId: string): string => { return this.baseURL + 'approve-order/' + orderId },
        placeOrderAPI: this.baseURL + 'order/checkout/init/',
        verifyPlacedOrderAPI: (orderId: string): string => { return this.baseURL + 'order/checkout/confirm/' + orderId },
        generateOrderRecieptPdfForBuyerAPI: (orderId: string): string => { return this.baseURL + 'generate-order-receipt-for-buyer/' + orderId },
        generateOrderRecieptPdfForSellerAPI: (orderId: string): string => { return this.baseURL + 'generate-order-receipt-for-seller/' + orderId },
        generateTaxInvoiceForBuyerAPI: (orderId: string): string => { return this.baseURL + 'generate-tax-invoice-for-buyer/' + orderId },
        generateTaxInvoiceForSellerAPI: (orderId: string): string => { return this.baseURL + 'generate-tax-invoice-for-seller/' + orderId },
        markAllRevisionsAsReadAPI: (orderId: string): string => { return this.baseURL + 'order/mark-revision-seen/' + orderId }
    }

    // order API URLs
    adminAPIs = {
        adminLoginAPI: this.baseURL + 'admin/login/',
        orderByIDAPI: (orderId: string): string => { return this.baseURL + 'admin/order/' + orderId },
        paymentByIDAPI: (Id: string): string => { return this.baseURL + 'admin/payment/' + Id },
        sellerPayableOrdersAPI: this.baseURL + 'admin/seller-payable-orders/',
        makePaymentEntryForOrderAPI: (orderId: string): string => { return this.baseURL + 'admin/make-payment-entry/' + orderId },
        dbMetricsAPI: this.baseURL + 'admin/db-metrics/',
        ordersByDateRangeAPI: this.baseURL + 'admin/orders/date-range/',
        ordersByBuyerIdAPI: (buyerId: string): string => { return this.baseURL + 'admin/orders/buyer/' + buyerId },
        ordersBySellerIdAPI: (sellerId: string): string => { return this.baseURL + 'admin/orders/seller/' + sellerId },
        
        // Buyer management
        buyerByIdAPI: (buyerId: string): string => { return this.baseURL + 'admin/buyer/id/' + buyerId },
        buyerByEmailAPI: (email: string): string => { return this.baseURL + 'admin/buyer/email/' + email },
        buyersByDateRangeAPI: this.baseURL + 'admin/buyers/date-range/',
        
        // Seller management
        sellerByIdAPI: (sellerId: string): string => { return this.baseURL + 'admin/seller/id/' + sellerId },
        sellerByEmailAPI: (email: string): string => { return this.baseURL + 'admin/seller/email/' + email },
        sellersByDateRangeAPI: this.baseURL + 'admin/sellers/date-range/',
        
        // Delivery management
        deliveryByIdAPI: (deliveryId: string): string => { return this.baseURL + 'admin/delivery/' + deliveryId },
        deliveryByOrderIdAPI: (orderId: string): string => { return this.baseURL + 'admin/delivery/order/' + orderId },
        
        // Seller Plan management
        sellerPlanAPI: this.baseURL + 'admin/seller-plan/',
        sellerPlanByIdAPI: (planId: string): string => { return this.baseURL + 'admin/seller-plan/' + planId },
        activeSellerPlansAPI: this.baseURL + 'admin/seller-plan/active',

        // Stream Chat monitoring
        streamChatAdminTokenAPI: this.baseURL + 'admin/stream-chat/admin-token',
        streamChatChannelAPI: (channelId: string): string => { return this.baseURL + 'admin/stream-chat/channel/' + channelId },
    }

    // promotions API URLs
    promoAPIs = {
        promoAPI:  this.baseURL + 'promo/',
        allPromoAPI:  this.baseURL + 'promo/',
        activePromoAPI:  this.baseURL + 'active-promo/',
        editPromo: (promoId: string): string => { return this.baseURL + 'promo/' + promoId },
        deletePromo: (promoId: string): string => { return this.baseURL + 'promo/' + promoId },
    }


    // seller API URLs
    sellerAPIs =  {
        createSellerAPI: this.baseURL + 'create-seller/',
        updateSellerAPI: this.baseURL + 'update-seller/',
        getSellerByAuthIdAPI: this.baseURL + 'get-seller-by-auth/',
        getSellerByIdAPI: this.baseURL + 'get-seller-by-id/',
        getSellerFormData: this.baseURL + 'seller/form-data/',
        saveSellerFormData: this.baseURL + 'seller/form-data/',
        sellerMetricsAPI: this.baseURL + 'seller-metrics-by-id/',
        getSellerPlanByIdAPI: (planId: string): string => { return this.baseURL + 'seller-plan/' + planId }
    }


    // streamChat API URLs
    streamChatAPIs = {
        streamTokenAPI:  this.baseURL + 'stream_token/',
        channelAPI:  this.baseURL + 'channel/',
        sendTextAPI:  this.baseURL + 'sendText/',
        sendFileAPI:  this.baseURL + 'sendFile/',
        sendMetaDataAPI:  this.baseURL + 'sendMetaData/',
    }


    // ActionCard API URLs
    ActionCardAPIs = {
        actionCardAPI: this.baseURL + 'action-cards/',
        saveActionCardAPI: this.baseURL + 'action-cards/',
        actionCardsByChanneAPI: this.baseURL + 'action-cards/channel/',
        getActionCardSummaryBreakdownAPI: this.baseURL + 'action-card/checkout/breakdown'
    }


    // subCategories API URLs
    subCategoriesAPIs = {
        subcategoriesAPI: this.baseURL + 'subcategories/',
        getSubcategoriesFromCategoryURL: (categoryID: string): string => { return this.baseURL + categoryID + '/subcategories' }
    };
  
    uploadAPIs = {
        createSessionAPI: this.baseURL + 'upload/session/',
        createAdminSessionAPI: this.baseURL + 'upload/admin/session/',
        sessionByIdAPI: (sessionId: string): string => this.baseURL + 'upload/session/' + sessionId,
        partsAPI: (sessionId: string): string => this.baseURL + 'upload/session/' + sessionId + '/parts',
        pauseSessionAPI: (sessionId: string): string => this.baseURL + 'upload/session/' + sessionId + '/pause',
        resumeSessionAPI: (sessionId: string): string => this.baseURL + 'upload/session/' + sessionId + '/resume',
        completeSessionAPI: (sessionId: string): string => this.baseURL + 'upload/session/' + sessionId + '/complete',
        completeAdminSessionAPI: (sessionId: string): string => this.baseURL + 'upload/admin/session/' + sessionId + '/complete',
        assetReadUrlAPI: (assetId: string): string => this.baseURL + 'upload/asset/' + assetId + '/read-url',
    }
    
    // Delivery API URLs
    deliveryAPIs = {
        getBuyerDelivery: (orderId: string): string => { return this.baseURL + 'delivery/buyer/' + orderId },
        getSellerDelivery: (orderId: string): string => { return this.baseURL + 'delivery/seller/' + orderId },
        createDelivery: (orderId: string): string => { return this.baseURL + 'delivery/' + orderId }
    }

    // Revision API URLs
    revisionAPIs = {
        askForRevisionAPI: (orderId: string): string => { return this.baseURL + 'revision/checkout/init/' + orderId },
        verifyPlacedRevisionAPI: (revisionId: string): string => { return this.baseURL + 'revision/checkout/confirm/' + revisionId },
        getRevisionsByOrderId: (orderId: string): string => { return this.baseURL + 'revision/order/' + orderId },
        getRevisionsByDeliveryId: (deliveryId: string): string => { return this.baseURL + 'revision/delivery/' + deliveryId },
        getRevisionsByChannelId: (channelId: string): string => { return this.baseURL + 'revision/channel/' + channelId },
        getRevisionById: (revisionId: string): string => { return this.baseURL + 'revision/' + revisionId },
        updateRevisionById: (revisionId: string): string => { return this.baseURL + 'revision/' + revisionId },
        deleteRevisionById: (revisionId: string): string => { return this.baseURL + 'revision/' + revisionId },
    }
    
    // Notification API URLs
    NotificationAPIs = {
        createNotificationAPI: (userId: string): string => { return this.baseURL + 'notifications/' + userId},
        getNotificationsForUserAPI: this.baseURL + 'notifications/',
        markNotificationAsReadAPI: (notificationId: string): string => { return this.baseURL + 'notifications/' + notificationId + '/read'},
        markAllNotificationsAsReadAPI: this.baseURL + 'notifications/read-all',
        deleteNotificationAPI: (notificationId: string): string => { return this.baseURL + 'notifications/' + notificationId},
        getNavbarNotificationsAPI: this.baseURL + 'notifications/unread',
    }

    // Review API URLs
    ReviewAPIs = {
        createReviewAPI: this.baseURL + "review/create/",
        getReviewsByGigAPI: (gigId: string): string => { return this.baseURL + 'review/' + gigId },
        getReviewsByReviewerAPI: (reviewerId: string): string => { return this.baseURL + 'review/' + reviewerId },
        updateReviewAPI: (id: string): string => { return this.baseURL + 'review/update/' + id },
        deleteReviewAPI: (id: string): string => { return this.baseURL + 'review/' + id },
        getAverageRatingForGigAPI: (gigId: string): string => { return this.baseURL + 'average-rating/' + gigId },
    }

    // Progress Review API URLs
    ProgressReviewAPIs = {
        getReviewByIdAPI: (reviewId: string): string => { return this.baseURL + 'progress-review/' + reviewId },
        getReviewsForChannelAPI: (channelId: string): string => { return this.baseURL + 'progress-review/channel/' + channelId },
        getReviewsForOrderAPI: (orderId: string): string => { return this.baseURL + 'progress-review/order/' + orderId },
        postReviewAPI: this.baseURL + 'progress-review/',
        askForProgressReviewAPI: (orderId: string): string => { return this.baseURL + 'progress-review/ask/order/' + orderId },
    }

    feedbackAPIs = {
        createCategorySuggestionAPI: this.baseServiceDBURL + "category-suggestion/create",
        createWriteToUsFeedbackAPI: this.baseServiceDBURL + "write-to-us/create/",
        createGGReviewAPI: this.baseServiceDBURL + "gg-review/create"
    }

    // Service DB Admin API URLs (admin-only read/mark-read routes)
    serviceDBAdminAPIs = {
        // Category Suggestions
        allCategorySuggestionsAPI: this.baseServiceDBURL + 'category-suggestion/',
        unreadCategorySuggestionsAPI: this.baseServiceDBURL + 'category-suggestion/unread',
        readCategorySuggestionsAPI: this.baseServiceDBURL + 'category-suggestion/read',
        markCategorySuggestionReadAPI: (id: string): string => this.baseServiceDBURL + 'category-suggestion/' + id + '/mark-read',

        // GG Reviews
        allGGReviewsAPI: this.baseServiceDBURL + 'gg-review/',
        unreadGGReviewsAPI: this.baseServiceDBURL + 'gg-review/unread',
        readGGReviewsAPI: this.baseServiceDBURL + 'gg-review/read',
        ggReviewsByRatingAPI: (rating: number): string => this.baseServiceDBURL + 'gg-review/by-rating/' + rating,
        markGGReviewReadAPI: (id: string): string => this.baseServiceDBURL + 'gg-review/' + id + '/mark-read',

        // Write To Us
        allWriteToUsAPI: this.baseServiceDBURL + 'write-to-us/',
        unreadWriteToUsAPI: this.baseServiceDBURL + 'write-to-us/unread',
        readWriteToUsAPI: this.baseServiceDBURL + 'write-to-us/read',
        markWriteToUsReadAPI: (id: string): string => this.baseServiceDBURL + 'write-to-us/' + id + '/mark-read',
    }

    // Institute API URLs
    instituteAPIs = {
        searchInstitutesAPI: this.baseURL + 'institutes/search',
    }

    // GenieAI assistant API URLs
    genieAPIs = {
        chatAPI: this.baseURL + 'genie/chat',
    }
} 
